/**
 * Pure reputation math (P7) — shared by the client (display/preview), the server
 * (authoritative computation), and the verify harnesses.
 *
 * ⚠️ MIRROR of `server/src/lib/trust_graph.ts` — the SpacetimeDB module cannot
 * import across the monorepo boundary, so the two pure helpers the server needs
 * are duplicated there. Keep them in sync (CLAUDE.md documents the rule). The
 * client and harnesses import THIS copy; the server re-checks with its mirror,
 * so a vouch weight previewed in the UI matches the weight the reducer stamps.
 *
 * Everything here is integer-exact (no floating point). Influence balances are
 * bigint; scores are plain numbers in their documented integer ranges.
 */

import {
  TRUST_MIN,
  TRUST_MAX,
  VOUCH_WEIGHT_INF_FULL,
  VOUCH_WEIGHT_MIN_X100,
  VOUCH_WEIGHT_MAX_X100,
  TRAITOR_TAG_MICROS,
  COLLAB_TRAITOR_REDUCTION_BPS,
  CAUTION_REVIEW_COLLAB_REDUCTION_BPS,
  MAX_CAUTION_PENALTIES,
  COLLAB_ELIGIBILITY_FLOOR_X100,
  COLLAB_ELIGIBILITY_FULL_X100,
} from './constants.js';

const BPS_DENOM = 10_000n;

/** Clamp a number into an inclusive integer range. */
export function clampInt(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Clamp a raw value into the valid Trustworthiness band [0, 100]. */
export function clampTrust(v: number): number {
  return clampInt(Math.trunc(v), TRUST_MIN, TRUST_MAX);
}

/**
 * The weight (×100) a single vouch carries, as a function of the VOUCHER's
 * standing at vouch time. Standing combines the Influence they can stake (the
 * resource) with their own Trustworthiness (the reputation):
 *
 *   • the Influence term scales linearly from 0 up to `VOUCH_WEIGHT_INF_FULL`
 *     (a voucher with no Influence lends ~no weight; one at/above the cap lends
 *     full weight) — this is what structurally down-weights low-Influence (alt)
 *     vouches;
 *   • the Trustworthiness term scales the result around neutral 50 (a neutral
 *     voucher → ~1.0×, a maximally-trusted one → ~2.0×, an untrusted one → 0×).
 *
 * The result is clamped to [VOUCH_WEIGHT_MIN_X100, VOUCH_WEIGHT_MAX_X100] so a
 * vouch always counts at least a little and never dominates the graph.
 */
export function vouchWeightX100(voucherInf: bigint, voucherTrust: number): number {
  const inf = voucherInf < 0n ? 0n : voucherInf > VOUCH_WEIGHT_INF_FULL ? VOUCH_WEIGHT_INF_FULL : voucherInf;
  // Influence term: 0..100 (×100 of a 0..1 fraction of the saturation cap).
  const infTermX100 = Number((inf * 100n) / VOUCH_WEIGHT_INF_FULL);
  // Trustworthiness term: neutral 50 → 1.0, so divide by 50.
  const trust = clampTrust(voucherTrust);
  const raw = Math.floor((infTermX100 * trust) / 50);
  return clampInt(raw, VOUCH_WEIGHT_MIN_X100, VOUCH_WEIGHT_MAX_X100);
}

/** True iff a Traitor tag set to expire at `traitorUntilMicros` is still active. */
export function isTraitorActive(traitorUntilMicros: bigint, nowMicros: bigint): boolean {
  return traitorUntilMicros > 0n && nowMicros < traitorUntilMicros;
}

/** Micros remaining on an active Traitor tag (0 if not active). */
export function traitorRemainingMicros(traitorUntilMicros: bigint, nowMicros: bigint): bigint {
  if (!isTraitorActive(traitorUntilMicros, nowMicros)) return 0n;
  return traitorUntilMicros - nowMicros;
}

/** Whole days remaining on an active Traitor tag (rounded up; 0 if not active). */
export function traitorDaysRemaining(traitorUntilMicros: bigint, nowMicros: bigint): number {
  const rem = traitorRemainingMicros(traitorUntilMicros, nowMicros);
  if (rem <= 0n) return 0;
  const day = TRAITOR_TAG_MICROS / 30n; // 1 day in micros
  return Number((rem + day - 1n) / day);
}

/**
 * Collaboration eligibility (×100, i.e. a percent). This is the value a future
 * "collaboration request" check consults: 100 means fully collaborable, lower
 * means a partner is correspondingly less likely to accept.
 *
 *   • An active Traitor tag multiplies eligibility by 0.20 — the headline "reduce
 *     all future collaboration requests by 80%". It is attributable to the tag
 *     ALONE (not the numeric scores), so the 80% reduction is exact and visible.
 *   • Each Caution review (up to MAX_CAUTION_PENALTIES) further multiplies by
 *     0.80 — honest collaboration warnings compound, they are never hidden.
 *   • The result is floored at COLLAB_ELIGIBILITY_FLOOR_X100 (never absolute
 *     zero) — even a Traitor can eventually find someone, the cost is social.
 */
export function collabEligibilityX100(traitorActive: boolean, cautionCount: number): number {
  let x100 = BigInt(COLLAB_ELIGIBILITY_FULL_X100);
  if (traitorActive) {
    x100 = (x100 * (BPS_DENOM - COLLAB_TRAITOR_REDUCTION_BPS)) / BPS_DENOM;
  }
  const cautions = clampInt(Math.trunc(cautionCount), 0, MAX_CAUTION_PENALTIES);
  for (let i = 0; i < cautions; i++) {
    x100 = (x100 * (BPS_DENOM - CAUTION_REVIEW_COLLAB_REDUCTION_BPS)) / BPS_DENOM;
  }
  const v = Number(x100);
  return v < COLLAB_ELIGIBILITY_FLOOR_X100 ? COLLAB_ELIGIBILITY_FLOOR_X100 : v;
}
